import {
  Calendar,
  User,
  ArrowRight,
  Search,
  Loader2,
  Video,
  ArrowLeft,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
// import { ImageWithFallback } from "../figma/ImageWithFallback";
import { SitePagination } from "../ui/SitePagination";
import {
  blogsAPI,
  type BlogDetailItem,
  type BlogListItem,
} from "../../services/api";
import blogsImage from "../../assets/blogs.jpg";
import BlogDetailPage from "./BlogDetailPage";

const ITEMS_PER_PAGE = 6;

function formatBlogDate(dateStr: string | null): string {
  if (!dateStr) {
    return "—";
  }
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function mapApiBlogToPost(blog: BlogListItem) {
  const excerpt = blog.description?.trim() ?? blog.sub_title?.trim() ?? "";
  return {
    id: blog.id,
    title: blog.title,
    excerpt: excerpt ? excerpt : "No description.",
    category: blog.category_name ?? "Uncategorized",
    author: "JBF",
    date: formatBlogDate(blog.date),
    readTime: "—",
    image: blog.banner ?? "",
    video: blog.video ?? "",
  };
}

export default function BlogsPage() {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [blogPosts, setBlogPosts] = useState<
    ReturnType<typeof mapApiBlogToPost>[]
  >([]);
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blogDetail, setBlogDetail] = useState<BlogDetailItem | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailPage, setShowDetailPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(blogPosts.length / ITEMS_PER_PAGE));
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return blogPosts.slice(start, start + ITEMS_PER_PAGE);
  }, [blogPosts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, debouncedSearch]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(blogPosts.length / ITEMS_PER_PAGE));
    setCurrentPage(prev => (prev > tp ? tp : prev));
  }, [blogPosts.length]);

  // Load categories once
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await blogsAPI.getBlogList();
        const enabled = data.filter(b => b.is_enabled);
        const categoryNames = [
          "All",
          ...Array.from(
            new Set(
              enabled.map(b => b.category_name).filter(Boolean) as string[]
            )
          ).sort(),
        ];
        setCategories(categoryNames);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Dynamic fetch based on filters
  useEffect(() => {
    let cancelled = false;
    const fetchBlogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, any> = {};
        if (selectedCategory !== "All") {
          params.category_name = selectedCategory;
        }
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }

        const data = await blogsAPI.getBlogList(params);
        if (cancelled) {
          return;
        }

        const enabled = data.filter(b => b.is_enabled);
        const mapped = enabled.map(b => mapApiBlogToPost(b));
        setBlogPosts(mapped);
      } catch {
        if (!cancelled) {
          setError("Failed to load blogs.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBlogs();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory, debouncedSearch]);

  const openBlogDetail = async (blogId: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowDetailPage(true);
    setBlogDetail(null);
    setDetailError(null);
    setLoadingDetail(true);
    try {
      const detail = await blogsAPI.getBlogDetail(String(blogId));
      setBlogDetail(detail);
    } catch {
      setDetailError("Failed to load blog details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle URL ID for deep linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blogId = params.get("id");
    if (blogId && !blogDetail) {
      void openBlogDetail(Number(blogId));
    }
  }, [location.search, blogDetail]);

  // const shareBlog = (platform: string, blog: BlogDetailItem) => {
  //   const url =
  //     window.location.origin + window.location.pathname + "?id=" + blog.id;
  //   const dateStr = formatBlogDate(blog.date);
  //   const categoryStr = blog.category_name ?? "Uncategorized";

  //   // Create a rich text description with ALL details
  //   const shareTitle = `📢 *${blog.title}*`;
  //   const shareSubtitle = blog.sub_title ? `\n_${blog.sub_title}_` : "";
  //   const shareMeta = `\n\n📂 *Category:* ${categoryStr}\n🗓️ *Date:* ${dateStr}`;
  //   const shareBanner = blog.banner ? `\n🖼️ *Banner:* ${blog.banner}` : "";
  //   const shareDesc = blog.description
  //     ? `\n\n📝 *Description:*\n${blog.description}`
  //     : "";

  //   const fullMessage = `${shareTitle}${shareSubtitle}${shareMeta}${shareBanner}${shareDesc}\n\n🔗 *Read more at:* ${url}`;

  //   let shareUrl = "";
  //   switch (platform) {
  //     case "facebook":
  //       // Facebook sharer primarily uses the URL and scrapes OG tags
  //       shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
  //       break;
  //     case "instagram":
  //       shareUrl = `https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
  //       return;
  //     case "linkedin":
  //       shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullMessage)}`;
  //       break;
  //     case "whatsapp":
  //       shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(fullMessage)}`;
  //       break;
  //     case "copy":
  //       void navigator.clipboard.writeText(url);
  //       notify.success("Link copied to clipboard!");
  //       return;
  //   }

  //   if (shareUrl) {
  //     window.open(shareUrl, "_blank", "width=600,height=400");
  //   }
  // };

  if (showDetailPage) {
    if (loadingDetail) {
      return (
        <div className="min-h-screen bg-[#FFF9F0]">
          <div className="sticky top-[5rem] z-50 border-b border-gray-100 bg-white/80 backdrop-blur-md lg:top-[8.5rem]">
            <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
              <button
                onClick={() => setShowDetailPage(false)}
                className="group flex items-center gap-2 text-gray-600 transition-colors hover:text-[#7A1E1E]"
              >
                <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
                <span className="font-medium">Back to Blogs</span>
              </button>
            </div>
          </div>
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-[#7A1E1E]" />
              <p className="animate-pulse text-gray-500">
                Loading amazing stories...
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="min-h-screen bg-[#FFF9F0]">
          <div className="sticky top-[5rem] z-50 border-b border-gray-100 bg-white/80 backdrop-blur-md lg:top-[8.5rem]">
            <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
              <button
                onClick={() => setShowDetailPage(false)}
                className="group flex items-center gap-2 text-gray-600 transition-colors hover:text-[#7A1E1E]"
              >
                <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
                <span className="font-medium">Back to Blogs</span>
              </button>
            </div>
          </div>
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="text-center">
              <p className="mb-4 text-xl text-red-600">{detailError}</p>
              <button
                onClick={() => setShowDetailPage(false)}
                className="rounded-lg bg-[#7A1E1E] px-6 py-2 text-white hover:bg-[#5A1616]"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (blogDetail) {
      return (
        <BlogDetailPage
          blog={blogDetail}
          onBack={() => {
            setShowDetailPage(false);
            setBlogDetail(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      );
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={blogsImage}
            alt="Contact communication"
            className="h-[163%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl lg:text-[4rem]">JBF Blogs</h1>
            <p className="text-xl text-gray-200">
              Insights, stories, and updates from the Jain Boarding Federation
              community
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="sticky top-[8.9rem] z-20 border-b border-gray-100 bg-white/80 shadow-sm backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Search Bar */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-gray-100 bg-gray-50/50 py-2 pl-10 pr-4 text-sm outline-none transition-all focus:border-[#7A1E1E]/30 focus:bg-white focus:ring-4 focus:ring-[#7A1E1E]/5"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-full border border-transparent px-4 py-1.5 text-sm transition-all ${selectedCategory === category
                      ? "bg-[#7A1E1E] text-white shadow-md shadow-[#7A1E1E]/20"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Blog Post */}
      {/* {featuredPost && selectedCategory === "All" && !searchQuery && (
        <section className="bg-gradient-to-br from-[#FFF9F0] to-white py-16">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="inline-block rounded-full bg-[#D4A017] px-4 py-2 text-sm text-white">
                Featured Article
              </div>
            </div>

            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div className="group relative h-[400px] overflow-hidden rounded-2xl">
                <ImageWithFallback
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>

              <div>
                <div className="mb-4 flex items-center gap-4">
                  <span className="rounded-full bg-[#7A1E1E]/10 px-3 py-1 text-sm text-[#7A1E1E]">
                    {featuredPost.category}
                  </span>
                  <span className="text-sm text-gray-500">
                    {featuredPost.readTime}
                  </span>
                </div>

                <h2 className="mb-4 text-3xl text-gray-900 lg:text-4xl">
                  {featuredPost.title}
                </h2>

                <p className="mb-6 text-lg text-gray-600">
                  {featuredPost.excerpt}
                </p>

                <div className="mb-8 flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{featuredPost.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{featuredPost.date}</span>
                  </div>
                </div>

                <button className="group inline-flex items-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-3 text-white transition-all hover:bg-[#5A1616]">
                  Read Full Article
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )} */}

      {/* Blog Grid */}
      <section className="bg-gradient-to-b from-white to-[#FFF5E6] py-16 pt-12">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
            </div>
          ) : error ? (
            <div className="py-20 text-center">
              <p className="text-lg text-red-600">{error}</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg text-gray-500">
                No blogs found matching your criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl text-gray-900">
                  {selectedCategory === "All"
                    ? "Latest Articles"
                    : `${selectedCategory} Articles`}
                </h2>
              </div>

              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {paginatedPosts.map(post => (
                  <article
                    key={post.id}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="relative h-48 overflow-hidden bg-black">
                      {post.video ? (
                        <div className="relative h-full w-full">
                          {(() => {
                            const videoUrl = post.video;
                            let videoId = "";
                            if (videoUrl.includes("youtu.be/")) {
                              videoId = videoUrl
                                .split("youtu.be/")[1]
                                ?.split("?")[0];
                            } else if (videoUrl.includes("v=")) {
                              videoId = videoUrl.split("v=")[1]?.split("&")[0];
                            } else if (videoUrl.includes("embed/")) {
                              videoId = videoUrl
                                .split("embed/")[1]
                                ?.split("?")[0];
                            }

                            if (videoId) {
                              return (
                                <img
                                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                  alt={post.title}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={e => {
                                    (e.target as HTMLImageElement).src =
                                      `https://img.youtube.com/vi/${videoId}/0.jpg`;
                                  }}
                                />
                              );
                            }
                            return (
                              <div className="flex h-full items-center justify-center text-sm text-white">
                                No Video
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 text-gray-400">
                          <Video className="h-12 w-12" />
                          <span className="mt-2 text-xs">
                            Video not available
                          </span>
                        </div>
                        /* <ImageWithFallback
                          src={post.image}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        /> */
                      )}
                      <div className="absolute left-4 top-4">
                        <span className="rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-[#7A1E1E] backdrop-blur-sm">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-5">
                      <div className="mb-3 flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{post.date}</span>
                        </div>
                        {/* <span>•</span>
                        <span>{post.readTime}</span> */}
                      </div>

                      <h3 className="mb-3 line-clamp-2 min-h-[3.5rem] text-xl font-semibold text-gray-900 transition-colors group-hover:text-[#7A1E1E]">
                        {post.title}
                      </h3>

                      <p className="mb-6 line-clamp-2 text-sm leading-relaxed text-gray-600">
                        {post.excerpt}
                      </p>

                      <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <User className="h-4 w-4" />
                          <span>{post.author}</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => openBlogDetail(post.id)}
                          className="group flex items-center gap-1 text-sm font-semibold text-[#7A1E1E] transition-colors hover:text-[#5A1616]"
                        >
                          Read More
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-10">
                <SitePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={blogPosts.length}
                  pageSize={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  itemLabel="blogs"
                />
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
